import AerisWeather from '@aerisweather/javascript-sdk'
import { CONTEXT_VERSION, LeafletProvider } from '@react-leaflet/core'
import {
  FitBoundsOptions,
  LatLngBoundsExpression,
  Map as LeafletMap,
  MapOptions,
  latLng
} from 'leaflet'
import isEmpty from 'lodash/isEmpty'
import React, {
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { AERISWEATHER_CONFIG } from './AereisMapConfig'

export interface MapContainerProps extends MapOptions {
  bounds?: LatLngBoundsExpression
  boundsOptions?: FitBoundsOptions
  children?: ReactNode
  className?: string
  id?: string
  placeholder?: ReactNode
  style?: CSSProperties
  whenCreated?: (map: LeafletMap) => void
  whenReady?: () => void
  mapInstance?: any
  aerisLayer?: { id: string; secret: string }
}

export function useMapElement(
  mapRef: MutableRefObject<HTMLElement | null>,
  props: MapContainerProps
): LeafletMap | null {
  let { aerisLayer } = props
  const [map, setMap] = useState<LeafletMap | null>(null)
  useEffect(() => {
    if (isEmpty(mapRef.current) && isEmpty(map) && aerisLayer) {
      const aeris = new AerisWeather(
        aerisLayer?.id || '',
        aerisLayer?.secret || ''
      )
      let instance: any = null
      aeris.apps(apps => {
        const app = new apps.InteractiveMapApp('#fareye-leaflet', {
          map: {
            strategy: 'leaflet',
            zoom: 4,
            layers: 'satellite,radar'
          },
          panels: {
            ...AERISWEATHER_CONFIG
          }
        })
        app.on('ready', () => {
          // get the native map instance from the underlying InteractiveMap instance
          const mapInstance = app?.map?.map
          instance = mapInstance
          if (props.center != null && props.zoom != null) {
            instance?.setView?.(props.center, props.zoom)
          } else if (props.bounds) {
            instance?.fitBounds?.(props.bounds, props.boundsOptions)
          }
          if (props.whenReady != null) {
            instance?.whenReady?.(props.whenReady)
          }
          setMap(instance)
        })
      })
    } else if (mapRef.current && !map) {
      const instance = new LeafletMap(mapRef.current, props)
      if (props.center != null && props.zoom != null) {
        instance.setView(props.center, props.zoom)
      } else if (props.bounds) {
        instance.fitBounds(props.bounds, props.boundsOptions)
      }
      if (props.whenReady) {
        instance.whenReady(props.whenReady)
      }
      setMap(instance)
    } else if (map) {
      if (!isEmpty(props.center) && props.zoom) {
        map.setView(latLng(props.center as any), props.zoom)
      } else if (props.bounds) {
        map.fitBounds(props.bounds, props.boundsOptions)
      } else {
        map.setView({ lat: 28, lng: 72 }, 5) // center for india
      }
    }
  }, [mapRef, map, props])

  return map
}

export function MapContainer<
  Props extends MapContainerProps = MapContainerProps
>({
  children,
  className,
  id,
  placeholder,
  style,
  whenCreated,
  ...options
}: Props) {
  const mapRef = useRef<HTMLDivElement>(null)
  const map = useMapElement(mapRef, options)
  const createdRef = useRef<boolean>(false)
  useEffect(() => {
    if (map != null && createdRef.current === false && whenCreated != null) {
      createdRef.current = true
      whenCreated(map)
    }
  }, [map, whenCreated])

  useEffect(() => {
    return () => {
      map?.remove()
    }
  }, [map])

  const [props] = useState({ className, id, style })
  const context = useMemo(
    () => (map ? { __version: CONTEXT_VERSION, map } : null),
    [map]
  )

  const contents = context ? (
    <LeafletProvider value={context}>{children}</LeafletProvider>
  ) : (
    placeholder ?? null
  )
  return (
    <div {...props} ref={mapRef}>
      {contents}
    </div>
  )
}
